import { useMemo, useState } from 'react';
import { browserToPdf, pdfToBrowser } from './placement.utils.ts';
import { resolveDragLanding } from './placementCollision.utils.ts';
import type { ViewportPoint } from './placementCollision.types.ts';
import type { BrowserBox, CanvasPageInfo } from './PlacementCanvas.types.ts';
import { buildDropPreview, sameDropPreview } from './PlacementOverlay.utils.ts';
import type { DropPreview } from './PlacementOverlay.types.ts';
import type {
	PlacementGestureHandlers,
	PlacementGestures,
	UsePlacementGesturesArgs,
} from './usePlacementGestures.types.ts';

/** Drag, drop and resize handling for the placed boxes on one page. */
export const usePlacementGestures = ({
	pageNum,
	dims,
	scale,
	collectPages,
	obstaclesOnPage,
	onPlacementChange,
}: UsePlacementGesturesArgs): PlacementGestures => {
	const [dropPreview, setDropPreview] = useState<DropPreview | null>(null);

	// Memoised apart from dropPreview: it changes on every drag tick, and these must not.
	const handlers = useMemo<PlacementGestureHandlers>(() => {
		const resolvePlacementLanding = (
			key: string,
			box: BrowserBox,
			centre: ViewportPoint,
			pages: CanvasPageInfo[]
		) =>
			resolveDragLanding({
				centre,
				sourcePageNum: pageNum,
				stampWidthPx: box.width,
				stampHeightPx: box.height,
				scale,
				pages,
				obstaclesOnPage: (target) => obstaclesOnPage(target, key),
			});

		return {
			previewPlacementDrop: (key, box, centre) => {
				const pages = collectPages();
				const landing = resolvePlacementLanding(key, box, centre, pages);
				const next = buildDropPreview({
					key,
					landing,
					pages,
					sourcePageNum: pageNum,
					scale,
				});
				setDropPreview((current) => (sameDropPreview(current, next) ? current : next));
			},
			commitPlacementDrop: (key, box, centre) => {
				setDropPreview(null);
				const landing = resolvePlacementLanding(key, box, centre, collectPages());
				// No landing: left unchanged, react-rnd puts the box back where it started.
				if (landing?.landing) {
					onPlacementChange(key, landing.pageNum, landing.landing);
				}
			},
			commitPlacementResize: (key, next) =>
				onPlacementChange(key, pageNum, browserToPdf(next, dims.heightPt, scale)),
			findResizeObstacles: (key) =>
				obstaclesOnPage(pageNum, key).map((obstacle) =>
					pdfToBrowser(obstacle, dims.heightPt, scale)
				),
		};
	}, [pageNum, dims, scale, collectPages, obstaclesOnPage, onPlacementChange]);

	return { dropPreview, handlers };
};
